import DOMPurify from 'dompurify';
import {
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

import type { TAiFindingAction } from '../types';

const useStyles = makeStyles({
  dialogSurface: {
    width: 'calc(100vw - 32px)',
    maxWidth: '1400px',
    maxHeight: 'calc(100vh - 32px)',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalXL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionLabel: {
    color: tokens.colorNeutralForeground3,
  },
  previewViewport: {
    maxHeight: 'calc(100vh - 250px)',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'auto',
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: tokens.spacingHorizontalL,
  },
});

const sanitizeSuggestionHtml = (html: string) =>
  DOMPurify.sanitize(html, {
    ADD_ATTR: ['colwidth', 'data-id', 'data-border-style', 'data-border-weight', 'style'],
  });

type TProps = {
  action?: TAiFindingAction;
  onAccept: (action: TAiFindingAction) => void;
  onClose: () => void;
};

const MockAiFindingSuggestionDialog = ({ action, onAccept, onClose }: TProps) => {
  const classes = useStyles();

  return (
    <Dialog open={Boolean(action)} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={classes.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={<Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} onClick={onClose} />}
          >
            {action?.previewTitle ?? action?.label ?? 'Suggestion preview'}
          </DialogTitle>

          <DialogContent className={classes.dialogContent}>
            {action ? (
              <>
                <div className={classes.section}>
                  <Caption1 className={classes.sectionLabel}>Suggested change</Caption1>
                  <Body1>{action.previewDescription ?? action.label}</Body1>
                </div>

                {action.previewHtml ? (
                  <div className={classes.previewViewport}>
                    <div
                      className="document-content static-content"
                      dangerouslySetInnerHTML={{ __html: sanitizeSuggestionHtml(action.previewHtml) }}
                    />
                  </div>
                ) : null}
              </>
            ) : null}
          </DialogContent>

          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={() => action && onAccept(action)}>
              Accept
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default MockAiFindingSuggestionDialog;
